/**
 * useBoardSnapshot -- React Query hook for the board JSON endpoint
 * (P17.3 / #458).
 *
 * Mirrors backend/dossier/board_router.py::get_board_snapshot.
 * 60s staleTime -- the board view is exec-shaped, not operator-
 * shaped, so we don't need cascade's 15s polling. The user can
 * refresh manually if they want fresher numbers.
 *
 * Lives under shared/ because the board is a cross-module
 * composition (consumes twin + framework + foundation/mvb signals).
 * Same architectural posture as the DossierCard download.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { boardKeys } from "shared/api/queryClient";
import type { BoardSnapshot } from "shared/api/types";

export interface UseBoardSnapshotOptions {
  /** ISO-8601 datetime (tz-aware). When set, replays chain state. */
  as_of?: string | undefined;
  /**
   * Skip the request when the caller already knows it will be refused.
   *
   * The Home card hides itself for anyone but the approver, but it
   * called this first -- so every non-approver's Home fired a request
   * that 403d for a card they never saw. Harmless on screen, noisy in
   * the log, and it makes a "did anything refuse her?" check report a
   * refusal that no human ever met.
   */
  enabled?: boolean;
}

export function useBoardSnapshot(
  opts: UseBoardSnapshotOptions = {},
): UseQueryResult<BoardSnapshot, ApiError> {
  const path = opts.as_of
    ? `/v1/board/snapshot?as_of=${encodeURIComponent(opts.as_of)}`
    : "/v1/board/snapshot";
  return useQuery({
    queryKey: boardKeys.snapshot({ as_of: opts.as_of }),
    queryFn: () => api.get<BoardSnapshot>(path),
    staleTime: 60_000,
    retry: false,
    enabled: opts.enabled ?? true,
  });
}
